import { HStack, Spinner, Stack, Text, VStack, chakra } from "@chakra-ui/react"
import { Link as RouterLink, useParams, useMatch } from "react-router-dom"
import SectionCard from "../../framework/SectionCard"
import ErrorAlert from "../../components/ErrorAlert"
import { extractErrorInfo } from "../../lib/api/error-utils"
import { useResortFeature } from "../../hooks/useResortFeatures"
import FeatureInfoTab from "./tabs/FeatureInfoTab"
import FeatureMapTab from "./tabs/FeatureMapTab"
import FeatureJsonTab from "./tabs/FeatureJsonTab"

const StyledTabLink = chakra(RouterLink)

export default function FeatureDetail() {
  const { uid, featureUid } = useParams<{ uid: string; featureUid: string }>()
  const { data, isLoading, error } = useResortFeature(uid, featureUid)
  const resortName = data?.resortName || uid

  const infoMatch = useMatch("/resorts/:uid/features/:featureUid")
  const mapMatch = useMatch("/resorts/:uid/features/:featureUid/map")
  const jsonMatch = useMatch("/resorts/:uid/features/:featureUid/json")

  const errorInfo = error ? extractErrorInfo(error) : null

  return (
    <SectionCard>
      <Stack gap="4">
        <HStack gap="2" align="center" color="gray.300" fontSize="sm">
          <StyledTabLink to="/resorts" color="blue.200">
            Resorts
          </StyledTabLink>
          <Text>/</Text>
          <StyledTabLink to={`/resorts/${uid}`} color="blue.200">
            {resortName}
          </StyledTabLink>
          <Text>/</Text>
          <Text>{data?.name ?? featureUid ?? "Feature"}</Text>
        </HStack>

        {isLoading && (
          <HStack>
            <Spinner size="sm" />
            <Text>Loading feature…</Text>
          </HStack>
        )}

        {errorInfo && <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />}

        {data && (
          <VStack align="stretch" gap="6">
            <HStack gap="1" borderBottomWidth="1px" borderColor="gray.700" pb="0">
              <StyledTabLink
                to={`/resorts/${uid}/features/${featureUid}`}
                display="inline-flex"
                alignItems="center"
                px="4"
                py="2"
                fontSize="sm"
                fontWeight="medium"
                borderTopRadius="md"
                bg={infoMatch ? "blue.600" : "transparent"}
                color={infoMatch ? "white" : "gray.100"}
                _hover={{ bg: infoMatch ? "blue.600" : "blue.700", color: "white", textDecoration: "none" }}
              >
                Info
              </StyledTabLink>
              <StyledTabLink
                to={`/resorts/${uid}/features/${featureUid}/map`}
                display="inline-flex"
                alignItems="center"
                px="4"
                py="2"
                fontSize="sm"
                fontWeight="medium"
                borderTopRadius="md"
                bg={mapMatch ? "blue.600" : "transparent"}
                color={mapMatch ? "white" : "gray.100"}
                _hover={{ bg: mapMatch ? "blue.600" : "blue.700", color: "white", textDecoration: "none" }}
              >
                Map
              </StyledTabLink>
              <StyledTabLink
                to={`/resorts/${uid}/features/${featureUid}/json`}
                display="inline-flex"
                alignItems="center"
                px="4"
                py="2"
                fontSize="sm"
                fontWeight="medium"
                borderTopRadius="md"
                bg={jsonMatch ? "blue.600" : "transparent"}
                color={jsonMatch ? "white" : "gray.100"}
                _hover={{ bg: jsonMatch ? "blue.600" : "blue.700", color: "white", textDecoration: "none" }}
              >
                JSON
              </StyledTabLink>
            </HStack>

            {infoMatch && <FeatureInfoTab data={data} />}
            {mapMatch && <FeatureMapTab data={data} />}
            {jsonMatch && <FeatureJsonTab data={data} />}
          </VStack>
        )}
      </Stack>
    </SectionCard>
  )
}
